import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = "https://365luxuryhomes.com/api/list";
const getToken = () => localStorage.getItem("authToken");
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const ListRequestsPage = () => {
  const [data, setData] = useState({ requests: [], total: 0, page: 1, totalPages: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API, {
        params: { page, limit: 15, search },
        headers: authHeaders(),
      });
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }, [page, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const deleteRequest = async (id) => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/${id}`, { headers: authHeaders() });
      fetchData();
    } catch (e) {
      alert("Failed to delete");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchData();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-xl font-bold mb-4">List With Us - Submissions</h1>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex gap-2 mb-4">
        <input
          className="input input-bordered input-sm flex-1 max-w-xs"
          placeholder="Search by name, email, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" className="btn btn-primary btn-sm">
          Search
        </button>
        {search && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setSearch("");
              setPage(1);
            }}
          >
            Clear
          </button>
        )}
      </form>

      {/* Results count */}
      <p className="text-sm text-base-content/60 mb-3">
        {data.total} total submission{data.total !== 1 ? "s" : ""}
      </p>

      {/* Table */}
      <div className="bg-base-100 rounded-lg shadow-sm overflow-x-auto">
        {loading ? (
          <div className="flex justify-center p-8">
            <span className="loading loading-spinner loading-lg"></span>
          </div>
        ) : (
          <table className="table table-sm">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Property</th>
                <th>Beds</th>
                <th>Building</th>
                <th>Furnishing</th>
                <th>Message</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {data.requests.length === 0 ? (
                <tr>
                  <td
                    colSpan="11"
                    className="text-center py-8 text-base-content/50"
                  >
                    No submissions found.
                  </td>
                </tr>
              ) : (
                data.requests.map((req, idx) => (
                  <tr key={req._id} className="hover">
                    <td>{(page - 1) * 15 + idx + 1}</td>
                    <td className="font-medium whitespace-nowrap">
                      {req.firstName} {req.lastName}
                    </td>
                    <td className="text-xs">{req.email}</td>
                    <td className="text-xs whitespace-nowrap">
                      +{req.phoneNumber}
                    </td>
                    <td className="text-xs">{req.propertyType}</td>
                    <td className="text-xs">{req.bedrooms}</td>
                    <td className="text-xs">{req.buildingName}</td>
                    <td className="text-xs">{req.furnishingStatus}</td>
                    <td className="text-xs max-w-[200px] truncate">
                      {req.message || "-"}
                    </td>
                    <td className="text-xs whitespace-nowrap">
                      {formatDate(req._id ? new Date(parseInt(req._id.toString().substring(0, 8), 16) * 1000) : null)}
                    </td>
                    <td>
                      <button
                        className="btn btn-ghost btn-xs text-error"
                        onClick={() => deleteRequest(req._id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data.totalPages > 1 && (
        <div className="flex justify-center mt-4">
          <div className="join">
            <button
              className="join-item btn btn-sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Prev
            </button>
            {Array.from({ length: Math.min(data.totalPages, 10) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`join-item btn btn-sm ${
                    pageNum === page ? "btn-active" : ""
                  }`}
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              className="join-item btn btn-sm"
              disabled={page >= data.totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListRequestsPage;
